/**
 * Task Service
 * Handles all task-related API calls
 * @module services/tasks/taskService
 */

import httpClient from '@/utils/httpClient';
import { isValidId, createInvalidIdResponse } from '@/services/shared/validationHelpers';
import { ENDPOINTS, getEndpointPath } from '@/api/endpoints';

const taskService = {
  /**
   * Get all tasks with pagination
   * @param {Object} params - Query parameters (page, limit, etc.)
   * @returns {Promise} - Response data
   */
  getAll: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });
      const baseEndpoint = getEndpointPath(ENDPOINTS.TASKS.BASE);
      const endpoint = queryParams.toString() 
        ? `${baseEndpoint}?${queryParams}` 
        : baseEndpoint;
      const response = await httpClient.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching tasks:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Get task by ID
   * @param {string} id - Task ID
   * @returns {Promise} - Response data
   */
  getById: async (id) => {
    if (!isValidId(id, 'Task')) {
      return createInvalidIdResponse('Task');
    }
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.TASKS.BY_ID, id));
      return response.data;
    } catch (error) {
      console.error('Error fetching task:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Create a new task
   * @param {Object} taskData - Task data
   * @returns {Promise} - Response data
   */
  create: async (taskData) => {
    try {
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.TASKS.CREATE), taskData);
      return response.data;
    } catch (error) {
      console.error('Error creating task:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Update a task
   * @param {string} id - Task ID
   * @param {Object} taskData - Updated task data
   * @returns {Promise} - Response data
   */
  update: async (id, taskData) => {
    if (!isValidId(id, 'Task')) {
      return createInvalidIdResponse('Task');
    }
    try {
      const response = await httpClient.patch(getEndpointPath(ENDPOINTS.TASKS.UPDATE, id), taskData);
      return response.data;
    } catch (error) {
      console.error('Error updating task:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Delete a task
   * @param {string} id - Task ID
   * @returns {Promise} - Response data
   */
  delete: async (id) => {
    if (!isValidId(id, 'Task')) {
      return createInvalidIdResponse('Task');
    }
    try {
      const response = await httpClient.delete(getEndpointPath(ENDPOINTS.TASKS.DELETE, id));
      return response.data;
    } catch (error) {
      console.error('Error deleting task:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Get tasks by employee ID
   * @param {string} employeeId - Employee ID
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise} - Response data
   */
  getByEmployee: async (employeeId, page = 1, limit = 25) => {
    if (!isValidId(employeeId, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    try {
      const endpoint = getEndpointPath(ENDPOINTS.TASKS.BY_EMPLOYEE, employeeId, page, limit);
      const response = await httpClient.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching employee tasks:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Get tasks by department ID
   * @param {string} departmentId - Department ID
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @param {string} status - Optional status filter
   * @returns {Promise} - Response data
   */
  getByDepartment: async (departmentId, page = 1, limit = 25, status = null) => {
    if (!isValidId(departmentId, 'Department')) {
      return createInvalidIdResponse('Department');
    }
    try {
      const params = new URLSearchParams({ page, limit });
      if (status) params.append('status', status);
      const endpoint = `${getEndpointPath(ENDPOINTS.TASKS.BY_DEPARTMENT, departmentId, page, limit).split('?')[0]}?${params}`;
      const response = await httpClient.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching department tasks:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Add an update message to a task
   * @param {string} id - Task ID
   * @param {string} message - Update message
   * @returns {Promise} - Response data
   */
  addUpdate: async (id, message) => {
    if (!isValidId(id, 'Task')) {
      return createInvalidIdResponse('Task');
    }
    try {
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.TASKS.ADD_UPDATE, id), { message });
      return response.data;
    } catch (error) {
      console.error('Error adding task update:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Mark a task as done (completed)
   * @param {string} id - Task ID
   * @returns {Promise} - Response data
   */
  markDone: async (id) => {
    if (!isValidId(id, 'Task')) {
      return createInvalidIdResponse('Task');
    }
    try {
      const response = await httpClient.patch(getEndpointPath(ENDPOINTS.TASKS.MARK_DONE, id));
      return response.data;
    } catch (error) {
      console.error('Error marking task as done:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Request time extension for a task
   * @param {string} id - Task ID
   * @param {string} message - Message/reason for extension request
   * @returns {Promise} - Response data
   */
  requestTimeExtension: async (id, message) => {
    if (!isValidId(id, 'Task')) {
      return createInvalidIdResponse('Task');
    }
    try {
      const payload = { message };
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.TASKS.REQUEST_TIME_EXTENSION, id), payload);
      return response.data;
    } catch (error) {
      console.error('Error requesting time extension:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Get time extension requests
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @param {string} status - Filter by status (pending, approved, rejected)
   * @returns {Promise} - Response data
   */
  getTimeExtensionRequests: async (page = 1, limit = 25, status = '') => {
    try {
      const endpoint = getEndpointPath(ENDPOINTS.TASKS.TIME_EXTENSION_REQUESTS, page, limit, status);
      const response = await httpClient.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching time extension requests:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Respond to time extension request (approve or reject)
   * @param {string} requestId - Request ID
   * @param {string} status - Response status ('approved' or 'rejected')
   * @returns {Promise} - Response data
   */
  respondToTimeExtension: async (requestId, status) => {
    if (!isValidId(requestId, 'Request')) {
      return createInvalidIdResponse('Request');
    }
    if (status !== 'approved' && status !== 'rejected') {
      throw new Error('Status must be either "approved" or "rejected"');
    }
    try {
      const payload = { status };
      const response = await httpClient.patch(getEndpointPath(ENDPOINTS.TASKS.RESPOND_TIME_EXTENSION, requestId), payload);
      return response.data;
    } catch (error) {
      console.error('Error responding to time extension:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },
};

export default taskService;

